import { supabase } from '../utils/supabaseClient';
import { logger } from '../utils/logger';
import { generateGroupToken, normalizeGroupToken } from '../utils/groupToken';

export interface GroupRecord {
  id: string;
  token: string;
  lastGameId: string | null;
}

type GroupLookupRow = {
  group_id: string;
  game_groups: { token?: string | null; last_game_id?: string | null } | Array<{ token?: string | null; last_game_id?: string | null }> | null;
};

type UpsertGroupResultRow = {
  id?: string | null;
  token?: string | null;
  last_game_id?: string | null;
};

async function upsertGroupRow(token: string, gameId: string): Promise<GroupRecord | null> {
  const normalized = normalizeGroupToken(token);
  if (!normalized) return null;
  try {
    const { data, error } = await supabase
      .rpc('upsert_game_group', { token_param: normalized, game_id_param: gameId })
      .maybeSingle();

    if (error) {
      logger.error('GROUP_SERVICE_UPSERT_FAILED', error);
      return null;
    }

    const row = data as UpsertGroupResultRow | null;
    if (!row?.id) return null;

    return {
      id: row.id,
      token: row.token || normalized,
      lastGameId: row.last_game_id || gameId,
    };
  } catch (err) {
    logger.error('GROUP_SERVICE_UPSERT_EXCEPTION', { message: err instanceof Error ? err.message : String(err) });
    return null;
  }
}

export async function resolveGroupByToken(token: string | null | undefined): Promise<GroupRecord | null> {
  const normalized = normalizeGroupToken(token);
  if (!normalized) return null;

  try {
    const { data, error } = await supabase
      .from('game_groups')
      .select('id, token, last_game_id')
      .eq('token', normalized)
      .maybeSingle();

    if (error) {
      logger.error('GROUP_SERVICE_RESOLVE_FAILED', error);
      return null;
    }

    if (!data?.id) return null;

    return {
      id: data.id as string,
      token: data.token || normalized,
      lastGameId: data.last_game_id || null,
    };
  } catch (err) {
    logger.error('GROUP_SERVICE_RESOLVE_EXCEPTION', { message: err instanceof Error ? err.message : String(err) });
    return null;
  }
}

export async function createGroupWithGame(gameId: string, preferredToken?: string): Promise<GroupRecord | null> {
  const token = normalizeGroupToken(preferredToken) || generateGroupToken();

  return upsertGroupRow(token, gameId);
}

export async function attachGameToGroup(token: string | null | undefined, gameId: string): Promise<GroupRecord | null> {
  const normalized = normalizeGroupToken(token);
  if (!normalized) return null;

  try {
    const existing = await resolveGroupByToken(normalized);
    if (!existing) {
      return upsertGroupRow(normalized, gameId);
    }

    const updated = await upsertGroupRow(normalized, gameId);
    return updated || { ...existing, lastGameId: gameId };
  } catch (err) {
    logger.error('GROUP_SERVICE_ATTACH_EXCEPTION', { message: err instanceof Error ? err.message : String(err) });
    return null;
  }
}

export async function findGroupByGame(gameId: string | null | undefined): Promise<GroupRecord | null> {
  if (!gameId) return null;

  try {
    const { data, error } = await supabase
      .from('game_group_games')
      .select('group_id, game_groups!inner(token, last_game_id)')
      .eq('game_id', gameId)
      .maybeSingle();

    if (error) {
      logger.error('GROUP_SERVICE_FIND_BY_GAME_FAILED', error);
      return null;
    }

    if (!data) return null;
    const row = data as unknown as GroupLookupRow;
    const groupData = Array.isArray(row.game_groups) ? row.game_groups[0] : row.game_groups;
    const token = groupData?.token || null;
    const lastGameId = groupData?.last_game_id || null;

    return {
      id: row.group_id as string,
      token: token || '',
      lastGameId: lastGameId || null,
    };
  } catch (err) {
    logger.error('GROUP_SERVICE_FIND_BY_GAME_EXCEPTION', { message: err instanceof Error ? err.message : String(err) });
    return null;
  }
}

